import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Ingest Bank Transactions from Document Step
 *
 * Reads extracted bank transaction data from a DocumentOCRResultV2,
 * upserts BankTransaction records (status: needs_review) for each
 * transaction, then runs AI customer resolution scoped to the rows it
 * just created so the review UI has a suggested customer.
 *
 * The extraction result is produced by extractDocumentContentV2Step using an
 * org-configured SchemaDefinition. This step expects the extracted result to
 * contain a `transactions` array with objects having these convention-based fields:
 *   - amount (number, required) — transaction credit amount
 *   - date (string, YYYY-MM-DD) — transaction date
 *   - merchant_name / payer_name (string) — who sent the payment
 *   - reference_number (string) — check number or transaction reference
 *   - payment_channel (string) — ach, wire, check, etc.
 *   - description (string) — transaction description/memo
 */
export const ingestBankTransactionsFromDocumentStep = defineStepConfig({
  type: "ingest_bank_transactions_from_document",
  name: "Ingest Bank Transactions from Document",
  description:
    "Upsert BankTransaction records from OCR results and resolve customers",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "The document containing extracted bank transaction data",
    ),
  }),

  outputSchema: z.object({
    ingestion: z.object({
      processed: z.number().describe("Number of bank transactions upserted"),
      skipped: z
        .number()
        .describe("Number of transactions skipped (no amount)"),
      errors: z.array(
        z.object({
          index: z.number(),
          error: z.string(),
        }),
      ),
    }),
    resolution: z.object({
      resolved: z.number().describe("Rows that got an AI customer match"),
      skipped: z.number().describe("Rows skipped (no merchant name available)"),
      errors: z.array(
        z.object({
          bankTransactionId: z.string(),
          error: z.string(),
        }),
      ),
    }),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 2,
      initialInterval: "10 seconds",
      backoffCoefficient: 2,
    },
  },
});
